import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useCart } from "../lib/CartContext";
import { useLang } from "../lib/LanguageContext";
import { BRAND } from "../lib/menuData";
import {
  Plus,
  Minus,
  Trash2,
  ShoppingBag,
  MessageCircle,
  Flame,
  Snowflake,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const formatRp = (k) =>
  `Rp ${(k * 1000).toLocaleString("id-ID")}`;

export const CartSheet = () => {
  const { t } = useLang();
  const {
    items,
    inc,
    dec,
    remove,
    clear,
    open,
    setOpen,
    totalQty,
    totalPrice,
  } = useCart();
  const [note, setNote] = useState("");
  const [pickup, setPickup] = useState("dinein");

  const checkout = () => {
    if (items.length === 0) return;
    const lines = items.map(
      (it) =>
        `• ${it.qty}x ${it.name}${it.variant && it.variant !== "Reg" ? ` (${it.variant})` : ""} — Rp ${(it.price * it.qty * 1000).toLocaleString("id-ID")}`
    );
    const body = [
      "Halo Hayalan Coffee! Saya mau pesan:",
      "",
      ...lines,
      "",
      `Total: ${formatRp(totalPrice)}`,
      `Tipe: ${pickup === "dinein" ? "Dine-in" : "Takeaway"}`,
      note ? `Catatan: ${note}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    const url = `https://wa.me/${BRAND.phone.replace("+", "")}?text=${encodeURIComponent(body)}`;
    window.open(url, "_blank");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-sheet"
        className="bg-brand-cream border-l border-brand-border w-full sm:max-w-md p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-brand-border text-left">
          <SheetTitle className="font-serif text-2xl md:text-3xl text-brand-ink tracking-tight flex items-center gap-3">
            <ShoppingBag
              className="w-5 h-5 text-brand-blue"
              strokeWidth={1.8}
            />
            {t.cart.title}
          </SheetTitle>
          <SheetDescription className="text-brand-mocha">
            {totalQty > 0
              ? `${totalQty} ${totalQty === 1 ? t.cart.item : t.cart.items}`
              : t.cart.empty_subtitle}
          </SheetDescription>
        </SheetHeader>

        {/* Items list */}
        <div
          className="flex-1 overflow-y-auto px-6 py-5"
          data-testid="cart-items-list"
        >
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <div className="w-16 h-16 rounded-full bg-brand-sand flex items-center justify-center mb-5">
                <ShoppingBag
                  className="w-7 h-7 text-brand-mocha"
                  strokeWidth={1.4}
                />
              </div>
              <h4 className="font-serif text-xl text-brand-ink">
                {t.cart.empty_title}
              </h4>
              <p className="mt-2 text-sm text-brand-mocha max-w-xs">
                {t.cart.empty_subtitle}
              </p>
            </div>
          ) : (
            <ul className="space-y-4">
              <AnimatePresence initial={false}>
                {items.map((it) => {
                  const kebab = it.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
                  const suffix =
                    it.variant && it.variant !== "Reg"
                      ? `-${it.variant.toLowerCase()}`
                      : "";
                  return (
                  <motion.li
                    key={it.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    transition={{ duration: 0.2 }}
                    data-testid={`cart-line-${kebab}${suffix}`}
                    className="flex gap-3 bg-white rounded-2xl p-3 border border-brand-border"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-brand-ink text-sm leading-snug truncate">
                            {it.name}
                          </p>
                          <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-brand-mocha">
                            {it.variant === "Hot" && (
                              <Flame
                                className="w-3 h-3 text-brand-coffee"
                                strokeWidth={2}
                              />
                            )}
                            {it.variant === "Ice" && (
                              <Snowflake
                                className="w-3 h-3 text-brand-blue"
                                strokeWidth={2}
                              />
                            )}
                            {it.variant === "Reg" ? it.category : it.variant}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => remove(it.id)}
                          data-testid={`cart-remove-${kebab}${suffix}`}
                          className="p-1.5 text-brand-mocha hover:text-red-600 transition-colors"
                          aria-label="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                        </button>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="inline-flex items-center gap-2 bg-brand-sand rounded-full p-1">
                          <button
                            type="button"
                            onClick={() => dec(it.id)}
                            data-testid={`cart-dec-${kebab}${suffix}`}
                            className="w-7 h-7 rounded-full bg-white border border-brand-border flex items-center justify-center text-brand-ink hover:bg-brand-coffee hover:text-white transition-colors"
                            aria-label="Decrease"
                          >
                            <Minus className="w-3 h-3" strokeWidth={2.2} />
                          </button>
                          <span className="text-sm font-medium text-brand-ink w-5 text-center">
                            {it.qty}
                          </span>
                          <button
                            type="button"
                            onClick={() => inc(it.id)}
                            data-testid={`cart-inc-${kebab}${suffix}`}
                            className="w-7 h-7 rounded-full bg-brand-blue text-white flex items-center justify-center hover:bg-blue-800 transition-colors"
                            aria-label="Increase"
                          >
                            <Plus className="w-3 h-3" strokeWidth={2.2} />
                          </button>
                        </div>
                        <span className="font-serif text-base text-brand-blue">
                          {formatRp(it.price * it.qty)}
                        </span>
                      </div>
                    </div>
                  </motion.li>
                  );
                })}
              </AnimatePresence>
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div
            className="border-t border-brand-border px-6 py-5 bg-white space-y-4"
            data-testid="cart-footer"
          >
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-brand-mocha">
                {t.cart.pickup_label}
              </Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2 p-1 bg-brand-sand rounded-full">
                <button
                  type="button"
                  onClick={() => setPickup("dinein")}
                  data-testid="pickup-dinein"
                  className={`py-2 rounded-full text-xs font-medium transition-all ${
                    pickup === "dinein"
                      ? "bg-brand-blue text-white"
                      : "text-brand-mocha hover:text-brand-ink"
                  }`}
                >
                  {t.cart.dinein}
                </button>
                <button
                  type="button"
                  onClick={() => setPickup("takeaway")}
                  data-testid="pickup-takeaway"
                  className={`py-2 rounded-full text-xs font-medium transition-all ${
                    pickup === "takeaway"
                      ? "bg-brand-blue text-white"
                      : "text-brand-mocha hover:text-brand-ink"
                  }`}
                >
                  {t.cart.takeaway}
                </button>
              </div>
            </div>

            <div>
              <Label
                htmlFor="cart-note"
                className="text-[11px] uppercase tracking-widest text-brand-mocha"
              >
                {t.cart.note_label}
              </Label>
              <Textarea
                id="cart-note"
                data-testid="cart-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t.cart.note_placeholder}
                className="mt-1.5 bg-brand-cream border-brand-border resize-none focus-visible:ring-brand-blue/40 text-sm"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="text-sm font-medium uppercase tracking-widest text-brand-mocha">
                {t.cart.total}
              </span>
              <span
                className="font-serif text-2xl text-brand-ink"
                data-testid="cart-total"
              >
                {formatRp(totalPrice)}
              </span>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={clear}
                data-testid="cart-clear"
                className="px-4 py-3 rounded-full text-sm font-medium text-brand-mocha hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                {t.cart.clear}
              </button>
              <button
                type="button"
                onClick={checkout}
                data-testid="cart-checkout"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-blue text-white px-5 py-3 rounded-full text-sm font-medium hover:bg-blue-800 transition-all hover:-translate-y-0.5"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
                {t.cart.checkout}
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
